import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "components/translations/useTranslation";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import OKCancelButtons from "components/modals/OKCancelButtons";
import Comment from "components/input/Comment.component";
import { addAttachment } from "components/modals/modal.actions";
import * as reduxSelectors from "components/store/application.reducers";
import { FormField } from "components/common/FormField.component";
import { TextInput } from "components/common/TextInput.component";
import { FileUploader, FileUploaderState, FileUploaderType } from "components/common/FileUploader.component";
import { ModalContentProps } from "types/modals";

const FILE_SIZE_LIMIT = 3 * 1024 * 1024; // 3 MB

interface AddAttachmentProps extends ModalContentProps {
    more: {
        tasks: Array<{ key: string }>;
    };
}

const getAcceptedFiles = (taskPermission: Record<string, boolean> | undefined) => {
    let extensions = ".png, .jpg, .jpeg";
    let hint = "popUp.addAttachment.browseHintPNG";
    let additionalTypes = "";

    if (taskPermission?.allowPdfUpload) {
        extensions += ", .pdf";
        hint = "popUp.addAttachment.browseHint";
    }
    if (taskPermission?.allowHtmlUpload) {
        extensions += ", .html";
        additionalTypes += ", HTML";
    }
    if (taskPermission?.allowTiffUpload) {
        extensions += ", .tif, .tiff";
        additionalTypes += ", TIFF";
    }

    return { extensions, hint, additionalTypes };
};

const AddAttachment = ({ more, closeModal, loopFocus, errorCallback }: AddAttachmentProps): React.ReactElement => {
    const { translate } = useTranslation();
    const titleInputRef = useRef<HTMLInputElement>(null);
    const taskDetails = useSelector(reduxSelectors.getTaskDetails);
    const dispatch = useDispatch();

    const [title, setTitle] = useState("");
    const [comment, setComment] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [actionRunning, setActionRunning] = useState(false);

    useEffect(() => {
        titleInputRef.current?.focus();
    }, []);

    const handleAddError = useCallback((errorMessage: string) => {
        setActionRunning(false);
        setUploadError(errorMessage);
        errorCallback({ error: errorMessage });
    }, [errorCallback]);

    const handleAdd = useCallback(() => {
        if (!file) return;
        setActionRunning(true);
        setUploadError(null);
        more.tasks.forEach(({ key }) => {
            const data = { key, title, comment, file, addMessage: translate("popUp.addAttachment.hasBeenAdded") };
            dispatch(addAttachment(data, closeModal, handleAddError));
        });
    }, [more.tasks, title, comment, file, translate, dispatch, closeModal, handleAddError]);

    const handleFileSelect = useCallback((selectedFile: File | null) => {
        setFile(selectedFile);
        if (selectedFile !== null) {
            setUploadError(null);
        }
    }, []);

    const acceptedFiles = getAcceptedFiles(taskDetails.taskPermission);
    const okEnabled = !!(comment && title && file && !actionRunning && !uploadError);

    return (
        <>
            <div className="ga-modal__content">
                <div className="w-100">
                    <FormField
                        id="title"
                        label={translate("popUp.addAttachment.fileTitleLabel")}
                        labelState={`(${translate("popUp.required")})`}
                    >
                        <TextInput
                            id="title"
                            ref={titleInputRef}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            tabIndex={MODAL_CONTENT_TAB_INDEX}
                        />
                    </FormField>
                </div>

                <div className="w-100">
                    <Comment
                        inputHint={translate("popUp.approve.commentHint")}
                        inputLimit={256}
                        translate={translate}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                        propagateValue={setComment}
                        id="comment"
                        label={translate("popUp.addAttachment.commentLabel")}
                        labelState={`(${translate("popUp.required")})`}
                    />
                </div>

                <div className="w-100">
                    <FileUploader
                        type={FileUploaderType.DEFAULT}
                        state={FileUploaderState.ENABLED}
                        header={{
                            label: translate("popUp.addAttachment.fileLabel") + ` (${translate("popUp.required")})`,
                            description: translate(acceptedFiles.hint) + acceptedFiles.additionalTypes
                        }}
                        acceptedFileExtensions={acceptedFiles.extensions}
                        maxFileSize={FILE_SIZE_LIMIT}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                        onFileSelect={handleFileSelect}
                        error={uploadError}
                        translate={translate}
                    />
                </div>
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 4}
                okEnabled={okEnabled}
                okCallback={handleAdd}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default AddAttachment;
